// src/components/drillholes/data/CollarsWorkspace.tsx

import { useState } from 'react';
import { Upload, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CollarsUploadDialog } from './CollarsUploadDialog';
import { uploadCollarsExcelUrl } from '@/api/urls';
import type { CollarSubView } from '../DrillholesApp';
// import type { Collar } from './DrillholesDataPanel';
import { cn } from '@/lib/utils';


export type Collar = {
  hole_id: string;
  x: number;
  y: number;
  z: number;
  logfile_id: string;
  filename: string;
  created_at: string;
};


interface Props {
  onCollarsLoaded:  (collars: Collar[]) => void;
  collars:          Collar[];
  loading:          boolean;
  subView:          CollarSubView;
  onSubViewChange:  (v: CollarSubView) => void;
}

// ── Sub-sidebar ───────────────────────────────────────────────────────────────

const SUB_ITEMS: { id: CollarSubView; label: string; icon: React.ReactNode }[] = [
  {
    id: 'coordinates',
    label: 'Coordinates',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <rect x="1" y="1" width="12" height="12" rx="1"/><line x1="1" y1="4" x2="13" y2="4"/><line x1="4" y1="1" x2="4" y2="13"/>
      </svg>
    ),
  },
  {
    id: 'sources',
    label: 'Source files',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <path d="M3 1h6l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M9 1v3h3"/>
      </svg>
    ),
  },
  {
    id: '3d',
    label: '3D view',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 1l6 3.5v5L7 13 1 9.5v-5L7 1z"/><path d="M7 1v12M1 4.5l6 3.5 6-3.5"/>
      </svg>
    ),
  },
];

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div
        onClick={onUpload}
        className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-border px-16 py-12 text-center cursor-pointer transition-colors hover:border-muted-foreground/40 hover:bg-muted/30"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted">
          <Upload size={20} className="text-muted-foreground" />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">Upload collars file</p>
          <p className="text-xs text-muted-foreground">CSV, XLS or XLSX</p>
        </div>
        <Button size="sm" variant="outline" tabIndex={-1}>Choose file</Button>
      </div>
    </div>
  );
}

// ── Coordinates table ─────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function CoordinatesView({ collars, onUpload }: { collars: Collar[]; onUpload: () => void }) {
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(0);

  if (collars.length === 0) return <EmptyState onUpload={onUpload} />;

  const q          = search.trim().toLowerCase();
  const filtered   = q ? collars.filter(c => c.hole_id.toLowerCase().includes(q)) : collars;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);
  const pageRows   = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(0); };

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border flex-shrink-0">
        <div className="relative flex-1 max-w-[220px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="5" cy="5" r="3.5"/><line x1="7.5" y1="7.5" x2="10.5" y2="10.5"/>
          </svg>
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Filter by hole ID…"
            className="w-full h-7 pl-7 pr-2 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <span className="text-xs text-muted-foreground ml-1">
          {filtered.length.toLocaleString()} hole{filtered.length !== 1 ? 's' : ''}
        </span>
        <div className="ml-auto">
          <Button size="sm" variant="outline" onClick={onUpload}>
            <Upload size={13} className="mr-1.5" />
            Add file
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
        <table className="w-full border-collapse text-[11px]" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-left text-[10px] font-medium text-muted-foreground w-[140px]">Hole ID</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-left text-[10px] font-medium text-muted-foreground">X</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-left text-[10px] font-medium text-muted-foreground">Y</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-left text-[10px] font-medium text-muted-foreground">Z</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-right text-[10px] font-medium text-muted-foreground w-[72px]"></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((c) => (
              <tr key={c.hole_id} className="group hover:bg-muted/40">
                <td className="px-3 py-1.5 border-b border-border font-mono text-foreground">{c.hole_id}</td>
                <td className="px-3 py-1.5 border-b border-border font-mono text-muted-foreground">{c.x.toFixed(2)}</td>
                <td className="px-3 py-1.5 border-b border-border font-mono text-muted-foreground">{c.y.toFixed(2)}</td>
                <td className="px-3 py-1.5 border-b border-border font-mono text-muted-foreground">{c.z.toFixed(2)}</td>
                <td className="px-3 py-1.5 border-b border-border text-right">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
                      <Pencil size={11} />
                    </button>
                    <button className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-border flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length.toLocaleString()}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(0)} disabled={safePage === 0} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">‹</button>
          <span className="px-2 text-xs text-foreground">{safePage + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={safePage === totalPages - 1} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">»</button>
        </div>
      </div>

    </div>
  );
}

// ── Source files ──────────────────────────────────────────────────────────────

function SourcesView({ collars, onUpload }: { collars: Collar[]; onUpload: () => void }) {
  if (collars.length === 0) return <EmptyState onUpload={onUpload} />;

  const byLogfile = collars.reduce<Record<string, Collar[]>>((acc, c) => {
    (acc[c.filename] = acc[c.filename] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="flex flex-col flex-1 min-h-0 p-4 gap-3 overflow-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{Object.keys(byLogfile).length} source{Object.keys(byLogfile).length !== 1 ? 's' : ''}</span>
        <Button size="sm" variant="outline" onClick={onUpload}>
          <Upload size={13} className="mr-1.5" />
          Add file
        </Button>
      </div>
      {Object.entries(byLogfile).map(([logfileId, lCollars]) => (
        <div key={logfileId} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-background">
          <div className="w-8 h-8 rounded-md bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="#185FA5" strokeWidth="1.2" strokeLinecap="round">
              <path d="M3 1h6l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z"/><path d="M9 1v3h3"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{logfileId}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {lCollars.length.toLocaleString()} holes
            </p>
          </div>
          <span className="text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded-full flex-shrink-0">
            {new Date(lCollars[0].created_at).toLocaleDateString()}
          </span>
          <div className="flex items-center gap-1 ml-2">
            <button className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
              <Pencil size={13} />
            </button>
            <button className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 3D view ───────────────────────────────────────────────────────────────────

function ThreeDView({ collars, onUpload }: { collars: Collar[]; onUpload: () => void }) {
  if (collars.length === 0) return <EmptyState onUpload={onUpload} />;
  return (
    <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
      3D viewer — coming soon
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function CollarsWorkspace({ onCollarsLoaded, collars, loading, subView, onSubViewChange }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const handleConfirm = async (file: File, params: Record<string, string | number>) => {
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.append(k, String(v));
      });
      const res = await fetch(`${uploadCollarsExcelUrl}?${query}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const loaded: Collar[] = await res.json();
      onCollarsLoaded(loaded);
      setDialogOpen(false);
      onSubViewChange('coordinates');
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const hasData = collars.length > 0;

  return (
    <div className="flex flex-1 min-h-0">

      {/* Sub-sidebar — desktop only, only when data exists */}
      {hasData && !loading && (
        <div className="hidden md:flex w-[220px] flex-shrink-0 px-3 pt-6 flex-col gap-1">
          {SUB_ITEMS.map((item) => {
            const active = subView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSubViewChange(item.id as CollarSubView)}
                className={cn(
                  'flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-sm transition-colors text-left',
                  active
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span className={cn(
                  'w-2 h-2 rounded-sm rotate-45 flex-shrink-0 transition-colors',
                  active ? 'bg-blue-500' : 'opacity-0'
                )} />
                {item.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Main panel */}
      <div className="flex flex-col flex-1 min-h-0 min-w-0">
        {loading && (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-border border-t-blue-500 animate-spin" />
          </div>
        )}
        {!loading && !hasData && !uploading && <EmptyState onUpload={() => setDialogOpen(true)} />}
        {uploading && (
          <div className="flex flex-1 items-center justify-center flex-col gap-4">
            <div className="w-8 h-8 rounded-full border-2 border-border border-t-blue-500 animate-spin" />
            <p className="text-sm text-muted-foreground">Processing file…</p>
          </div>
        )}
        {hasData && !uploading && subView === 'coordinates' && (
          <CoordinatesView collars={collars} onUpload={() => setDialogOpen(true)} />
        )}
        {hasData && !uploading && subView === 'sources' && (
          <SourcesView collars={collars} onUpload={() => setDialogOpen(true)} />
        )}
        {hasData && !uploading && subView === '3d' && (
          <ThreeDView collars={collars} onUpload={() => setDialogOpen(true)} />
        )}
        {error && (
          <p className="text-xs text-destructive px-4 py-2">{error}</p>
        )}
      </div>

      <CollarsUploadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={handleConfirm}
      />

    </div>
  );
}